import { useCallback, useEffect, useState } from 'react';

import { Box, useTheme } from '@material-ui/core';

import { MEDIA_TYPES, MediaTypeConfig } from 'constants/mediaTypes';
import { useAppSelector } from 'store';
import { AtendimentoInterface } from 'store/reducers/atendimentos';
import { canaisSelector, SelectChannelInterface } from 'store/reducers/canais';

import ChannelSelectField from './ChannelSelectField';
import FileCategory from './FileCategory';

interface TabPanelArquivosProps {
  contatoId: string;
  atendimento?: AtendimentoInterface;
  channelCode?: string;
  /**
   * Quando fornecido, o TabPanelArquivos delega a abertura do drawer ao pai
   * (ex: LeftGridDetalhesContato no modal da fila), permitindo renderizá-lo
   * inline sobre o left grid em vez de no document.body.
   */
  onOpenDrawer?: (params: {
    mediaType: MediaTypeConfig;
    contatoId: string;
    canalId: string;
  }) => void;
}

export default function TabPanelArquivos({
  contatoId,
  atendimento,
  channelCode: channelCodeProp,
  onOpenDrawer,
}: TabPanelArquivosProps) {
  const theme = useTheme();
  const [selectedChannelCode, setSelectedChannelCode] = useState<string>(
    channelCodeProp ?? '',
  );

  const { canaisSelect } = useAppSelector(canaisSelector);

  const handleCanalChange = useCallback((canal: SelectChannelInterface) => {
    setSelectedChannelCode(canal.code);
  }, []);

  useEffect(() => {
    // Se o canal já foi fornecido via prop, não precisa buscar
    if (channelCodeProp) {
      setSelectedChannelCode(channelCodeProp);
      return;
    }

    if (atendimento && canaisSelect) {
      const channel = canaisSelect.find(
        ({ id }) => id === atendimento.canal?.id,
      );

      setSelectedChannelCode(channel ? channel.code : '');
    }
  }, [atendimento, canaisSelect, channelCodeProp]);

  const handleOpenCategory = useCallback(
    (mediaType: MediaTypeConfig) => {
      if (onOpenDrawer) {
        onOpenDrawer({ mediaType, contatoId, canalId: selectedChannelCode });
      }
    },
    [contatoId, onOpenDrawer, selectedChannelCode],
  );

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      {!channelCodeProp && (
        <ChannelSelectField
          value={selectedChannelCode}
          onFilter={handleCanalChange}
          onReset={() => setSelectedChannelCode('')}
          style={{ width: '50%' }}
        />
      )}

      <Box>
        {MEDIA_TYPES.map(mediaType => (
          <FileCategory
            key={mediaType.key}
            mediaType={mediaType}
            color={theme.palette[mediaType.colorKey].main}
            contatoId={contatoId}
            canalId={selectedChannelCode}
            // Quando onOpenDrawer não está definido, omite onOpen para que
            // o FileCategory use o fallback via useModal (drawer global).
            onOpen={onOpenDrawer ? handleOpenCategory : undefined}
          />
        ))}
      </Box>
    </Box>
  );
}
